import {
    Avatar,
    Button,
    Grid,
    makeStyles,
    Paper,
    TextField,
    Typography,
    Box,
    Link
} from "@material-ui/core";
import { Color } from "@material-ui/lab";
import { Formik, Form } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomSelect } from "../../../components/customSelect";
import { clearCompanyError, fetchCompany, updateCompany } from "../../../redux/actions/companyActions";
import { fetchUser } from "../../../redux/actions/userActions";
import { AppStateType } from "../../../redux/reducers";
import { AppDispatchType } from "../../../redux/store";
import { Notification } from "../../../components/Notification"
import { useTranslation } from "react-i18next";
import { Alert } from "@material-ui/lab";
import { NavLink } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: "20px",
        marginTop: "20px",
        padding: "20px",
        flexGrow: 1,
    },
    large: {
        width: theme.spacing(15),
        height: theme.spacing(15),
        marginBottom: "10px",
        marginTop: "20px",
        margin: "0 auto",
    },
    marginTop: {
        marginTop: "10px",
    },
    center: {
        width: "100%",
        textAlign: "center",
    },
    button: {
        background: 'linear-gradient(90deg, #84FAB0 0%, #8FEAFE 100%)',
        boxShadow: '0px 0px 14px rgba(53, 64, 82, 0.05)'
    },
    feedback: {
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(4),
        marginRight: theme.spacing(4),
    },
    alert: {
        backgroundColor: "#354557",
        color: "#cfcfcf"
    },
    infoAlert: {
        "& .MuiAlert-icon": {
          color: "#B8E7FB",
        },
        marginTop: "15px",
        marginBottom: "15px",
        backgroundColor: "#26374d",
    }
}));

export const companySize = ["0-10", "50-250", "250-500", "500+"] as const;
// const sizeOptions = ["0 - 10 employees", "10 - 50 employees", "50 - 250 employees", "250 - 500 employees", "+500 employees"];

export function CompanyInformation() {
    const classes = useStyles();
    const { t } = useTranslation();
    const [notify, setNotify] = useState({ isOpen: false, message: "", type: "" as Color });
    const [submitted, setSubmitted] = useState(false);
    const error: any = useSelector(
        (state: AppStateType) => state.companyReducer.error
    );

    const typeOptions = [
        t("settingsForms_CompanyInformation_SelectOptions_Service"), 
        t("settingsForms_CompanyInformation_SelectOptions_Merchandising"), 
        t("settingsForms_CompanyInformation_SelectOptions_Manufacturing"), 
        t("settingsForms_CompanyInformation_SelectOptions_Other")
    ];

    const user: any = useSelector(
        (state: AppStateType) => state.authReducer.user
    );
    const userProfile: any = useSelector(
        (state: AppStateType) => state.userReducer.user
    );
    const company: any = useSelector(
        (state: AppStateType) => state.companyReducer.company
    );

    type CompanyForm = {
        name: string,
        email: string,
        type: string,
        registration_number: string,
        nr_employees: string,
        date_of_contract_signing: string,
        subscription_period: string,
        invoice_status: string,
        address: {
            address_line_1: string,
            city: string,
            country: string,
            floor: string,
            office_number: string,
            postcode: string,
            region_code: string,
        },
        imageUrl: string,
    }

    const dispatch = useDispatch();
    useEffect(() => {
        if (user) {
            dispatch(fetchUser(user.user, user.token));
        }
        return () => {
            dispatch(clearCompanyError());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userProfile) {
            dispatch(fetchCompany(userProfile.company, user.token));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfile]);

    const handleEmployees = (employees: number): string => {
        // "0-10", "50-250", "250-500", "500+"
        if (employees >= 0 && employees < 10) {
            return "0-10"
        } else {
            if (employees >= 10 && employees < 50) {
                return "10-50"
            } else {
                if (employees >= 50 && employees < 250) {
                    return "50-250"
                } else {
                    if (employees >= 250 && employees < 500) {
                        return "250-500"
                    } else {
                        if (employees >= 500) {
                            return "500+"
                        }
                    }
                }
            }
        }
        return ''
    }
    const getCompanyError = useCallback(() => {
        return Object.keys(error).map(key => `${key}:${error[key][0]}`).toString();
    }, [error]);
    const handleType = (type: string): string => {
        if (type === "hotel") {
            return t("settingsForms_CompanyInformation_SelectOptions_Other")
        }
        return '';
    }
    useEffect(() => {
        if (company) {
            const size = handleEmployees(company.nr_employees);
            const type = handleType(company.type);
            setInitialValues({
                name: company.name,
                type: type,
                nr_employees: size,
                address: company.address,
                date_of_contract_signing: company.date_of_contract_signing,
                subscription_period: company.subscription_period,
                registration_number: company.registration_number,
                invoice_status: company.invoice_status,
                imageUrl: company.logo
            });
        }
    }, [company])
    const [initialValues, setInitialValues] = useState<any>();

    const onSubmit = async (companyPayload: CompanyForm) => {
        const { imageUrl, ...payload } = companyPayload
        payload.type = company.type;
        // payload.nr_employees = company.nr_employees;
        payload.email = userProfile.email;
        setNotify({
            ...notify,
            isOpen: false,
        })
        dispatch(clearCompanyError());
        dispatch(async (dp: AppDispatchType) => {
            await updateCompany(company.id, user.token, payload)(dp);
            setNotify({
                isOpen: true,
                message: t("questionUnits_uploadSuccess"),
                type: "success"
            })
            setSubmitted(true);
        })

        // window.location.reload();
    };
    if (!initialValues) {
        return <div></div>;
    } else {
        return (
            <Paper className={classes.root}>
                <Typography variant="h4">{t("companyCard_companyInformation")}</Typography>
                <Alert className={classes.infoAlert} severity="info">
                    {t("settingsPage_alertMsgCompanyInformation_1")}
                    <Link component={NavLink} exact to="/customer-support"> {t("settingsPage_alertMsgCompanyInformation_2")} </Link>
                    {t("settingsPage_alertMsgCompanyInformation_3")}
                </Alert>
                <Formik<CompanyForm> initialValues={initialValues} onSubmit={onSubmit}>
                    {({ values, handleChange }) => {
                        return (
                            <Form>
                                <Grid container spacing={5} direction='row-reverse'>
                                    <Grid item xs={12} sm={4}>
                                        <Grid item xs={12}>
                                            <div className={classes.center}>
                                                <Avatar
                                                    alt="Avatar"
                                                    className={
                                                        classes.large
                                                    }
                                                    src={company.logo}
                                                />
                                            </div>
                                            <Typography
                                                align="center"
                                                style={{ fontSize: 11 }}
                                            >
                                                {t("settingsForms_ImageCantBeChanged")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                className={
                                                    classes.marginTop
                                                }
                                                fullWidth
                                                disabled={true}
                                                label={t("settingsForms_RegNumber_label")}
                                                id="registration_number"
                                                value={values.registration_number}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                className={
                                                    classes.marginTop
                                                }
                                                fullWidth
                                                disabled={true}
                                                label={t("settingsForms_InvoiceStatus_label")}
                                                id="invoice_status"
                                                value={
                                                    values.invoice_status
                                                }
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Grid container direction="column">
                                            <Grid item xs={12}>
                                                <TextField
                                                    className={
                                                        classes.marginTop
                                                    }
                                                    fullWidth
                                                    disabled={true}
                                                    label={t("settingsForms_CompanyName_label")}
                                                    id="companyName"
                                                    value={values.name}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                            <Grid
                                                direction="row"
                                                container
                                                spacing={5}
                                            >
                                                <Grid item xs={12} sm={6}>
                                                    <CustomSelect
                                                        inputLabel={t("settingsForms_CompanyType_label")}
                                                        value={values.type}
                                                        disabled={true}
                                                        options={typeOptions}
                                                        onChange={handleChange}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <CustomSelect
                                                        inputLabel={t("settingsForms_CompanySize_label")}
                                                        getOptionLabel={el => el + ` ${t("settingsForms_Employees_label")}`}
                                                        value={values.nr_employees}
                                                        disabled={true}
                                                        options={companySize as unknown as string[]}
                                                        onChange={handleChange}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    className={
                                                        classes.marginTop
                                                    }
                                                    fullWidth
                                                    label={t("settingsForms_CompanyAddress_label")}
                                                    id="address.address_line_1"
                                                    value={values.address && values.address.address_line_1 ? values.address.address_line_1 : ""}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                />
                                            </Grid>
                                            <Grid
                                                container
                                                direction="row"
                                                spacing={5}
                                            >
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        className={
                                                            classes.marginTop
                                                        }
                                                        fullWidth
                                                        disabled={true}
                                                        label={t("settingsForms_ContractDate_label")}
                                                        id="date_of_contract_signing"
                                                        value={values.date_of_contract_signing}
                                                        onChange={
                                                            handleChange
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        className={
                                                            classes.marginTop
                                                        }
                                                        fullWidth
                                                        disabled={true}
                                                        label={t("settingsForms_SubscriptionPeriod_label")}
                                                        id="subscription_period"
                                                        value={
                                                            values.subscription_period
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                </Grid>
                                {error && (
                                    <div className={classes.feedback}>
                                        {getCompanyError()}
                                    </div>
                                )}
                                {/* <Button
                                    variant="contained"
                                    type="submit"
                                    className={classes.button}
                                    style={{ marginTop: "20px" }}
                                    >
                                    {t("generalWords_SaveChanges")}
                                </Button> */}
                                {!error && submitted ? <Notification notify={notify} setNotify={setNotify} className={classes.alert} /> : null}
                            </Form>
                        );
                    }}
                </Formik>
            </Paper>
        );
    }
}
